import React, { useCallback, useRef, useState, useMemo } from "react";
import {
  Stack,
  useColorModeValue,
  useToast,
  HStack,
  Text,
  Circle,
  Tooltip,
  Box,
  Menu,
  MenuButton,
  MenuList,
  Button,
  Portal,
  MenuDivider,
  useDisclosure,
} from "@chakra-ui/react";
import { BiDotsVertical, BiShareAlt } from "react-icons/bi";
import { SmallActionButton } from "../../content/contentCanvas/cell/SmallActionButton";
import type { DealFinderTicker } from "../../../api/tickers/models/DealFinderTicker";
import { useDispatch } from "react-redux";
import { sendMessage } from "state/websocket/operations";
import { actions as pendingCreditsActions } from "state/pendingCredits/reducer";
import { actions as dealFinderActions } from "state/dealFinder/reducer";
import type { RequestEntities } from "types/charliui";
import { ToastMessageContent } from "screens/common/components";
import { v4 as uuid } from "uuid";
import { ShareEmailModal } from "./modals/ShareEmailModal";
import { PurchasingCartModal } from "./modals/PurchasingCartModal";
import type { IconType } from "react-icons";
import { IoEyeSharp, IoEyeOutline } from "react-icons/io5";
import { OptionsMenuItem } from "screens/collection/components/OptionsMenuItem";
import { useConversationContext } from "screens/thread/ConversationContext";
import { useConversationDialogSubmitFunctions } from "screens/thread/useConversationDialogSubmitFunctions";
import { TiThumbsUp } from "react-icons/ti";
import { IoMdThumbsUp } from "react-icons/io";
import { useEntitlementKey, useInvestmentDetailModal } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import { UpdateTickerPanel } from "../../landing/tabs/admin/tickers/components/UpdateTicker";
import type { UpdateTickerForm } from "../../landing/tabs/admin/tickers/components/UpdateTicker";
import type { UpdateTickerRequest } from "../../../api/tickers/models/UpdateTickerRequest";
import {
  updateTicker,
  getPaginatedTickers,
  generateTickerLogo,
  generateTickerBanner,
  generateDueDiligenceProject,
} from "../../../api/tickers";
import type { Ticker } from "../../../api/tickers/models/Ticker";
import { PiNetwork } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { useCheckDealRestricted, useCheckDealStatusAllowed, useCheckUserHasCredit } from "./utils/dealUtils";

interface DealMenuButtonsProps {
  deal: DealFinderTicker;
  iconSize?: string;
  showBorder?: boolean;
  displayOption?: "tile" | "list";
  isWideContainer?: boolean;
  isAnonymousUser?: boolean;
}

interface MenuButtonConfig {
  tooltip: string;
  icon: React.ComponentType;
  action?: string;
  message?: string;
  isActive?: boolean;
  likedCount?: number | null;
}

export const DealMenuButtons: React.FC<DealMenuButtonsProps> = ({
  deal,
  iconSize = "1rem",
  showBorder = true,
  displayOption,
  isWideContainer,
  isAnonymousUser,
}) => {
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const activeIconColor = useColorModeValue("primary.default", "primary.default");
  const activeBgCountColor = useColorModeValue("gray.100", "gray.600");
  const activeCountColor = useColorModeValue("gray.700", "gray.200");
  const defaultIconColor = useColorModeValue("gray.500", "gray.400");
  const dispatch = useDispatch();
  const toast = useToast();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({}); // Added loading state
  const { isOpen: isUpdateTickerOpen, onClose: onCloseUpdateTicker, onOpen: onOpenUpdateTicker } = useDisclosure();
  const [isLoadingUpdateTicker, setIsLoadingUpdateTicker] = useState(false);
  const [fetchedTicker, setFetchedTicker] = useState<Ticker | undefined>(undefined);
  const { onSubmitDueDiligenceProject, onSubmitDealFinderQuery } = useConversationDialogSubmitFunctions({});
  const { setSelectedQuestionAndFocus } = useConversationContext();
  const moreLikeThisCopy = useCopyValue("copy_deal_menu_more_like_this");
  const showCompetitorsCopy = useCopyValue("copy_deal_menu_show_competitors");
  const hasCompanyCatalogAdmin = useEntitlementKey("manage_ticker_write");
  const hasIRDashboardAccess = useEntitlementKey("ui_enable_ir_dashboard");
  const { isOpen, closeModal } = useInvestmentDetailModal();
  const navigate = useNavigate();

  const isRestricted = useCheckDealRestricted(deal.symbol);
  const hasEnoughCredit = useCheckUserHasCredit(deal.creditCosts?.analysis || null);
  const isDealStatusAllowed = useCheckDealStatusAllowed(deal.status);

  const menuButtons = useMemo<MenuButtonConfig[]>(
    () => [
      {
        tooltip: deal.hasUserLiked ? "Unlike deal" : "Like deal",
        icon: deal.hasUserLiked ? IoMdThumbsUp : TiThumbsUp,
        action: deal.hasUserLiked ? "unlike_deal" : "like_deal",
        message: deal.hasUserLiked ? `I'll unlike the ${deal.name} deal for you now.` : `I'll like the ${deal.name} deal for you now.`,
        isActive: deal.hasUserLiked,
        likedCount: deal.likeCount,
      },
      {
        tooltip: "Share deal",
        icon: BiShareAlt,
        // Share action is handled separately by modal trigger
        isActive: false,
      },
      {
        tooltip: deal.hasUserWatched ? `Unwatch ${deal.name}` : `Watch ${deal.name}`,
        icon: deal.hasUserWatched ? IoEyeSharp : IoEyeOutline,
        action: deal.hasUserWatched ? "unwatch_deal" : "watch_deal",
        message: deal.hasUserWatched
          ? `I'll remove the ${deal.name} deal from your watch list now.`
          : `I'll add the ${deal.name} deal to your watch list now.`,
        isActive: deal.hasUserWatched,
      },
    ],
    [deal.hasUserLiked, deal.name, deal.likeCount, deal.hasUserWatched]
  );

  const baseEntities: RequestEntities = useMemo(() => [{ entity: "deal_id", value: deal.id }], [deal.id]);

  const handleToastMessage = useCallback(
    (message: string) => {
      toast.closeAll();
      toast({
        render: ({ onClose }) => (
          <ToastMessageContent
            message={message}
            onClick={() => {
              onClose();
            }}
            onClose={onClose}
          />
        ),
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
    [toast]
  );

  const onSubmitWorkflow = useCallback(
    (intent: string, entities: RequestEntities, message?: string) => {
      if (!intent) return;
      const conversationId = uuid();

      try {
        dispatch(
          sendMessage({
            conversationId: conversationId,
            intent: `/${intent}`,
            entities,
          })
        );

        if (message) {
          handleToastMessage(message);
        }
      } catch (error) {
        handleToastMessage(`The following error occurred: ${error}. Please try again.`);
      }
    },
    [dispatch, handleToastMessage]
  );

  const submitDueDiligence = useCallback(
    (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      if (!deal || !deal.symbol || !deal.exchange) {
        return;
      }

      // Add pending credit deduction if there's a credit cost
      if (deal.creditCosts?.analysis && deal.creditCosts.analysis > 0) {
        dispatch(
          pendingCreditsActions.addPendingDeduction({
            id: uuid(),
            amount: deal.creditCosts.analysis,
          })
        );
      }

      setSelectedQuestionAndFocus({
        projectTicker: deal.symbol,
        projectExchange: deal.exchange,
      });

      onSubmitDueDiligenceProject({
        ticker: deal.symbol,
        exchange: deal.exchange,
        companyName: deal.name,
        companyDescription: deal.companyShortDescription || undefined,
        companyWebsite: deal.companyUrl || undefined,
        companyLogoUrl: deal.companyLogoUrl || undefined,
      });
    },
    [deal, setSelectedQuestionAndFocus, onSubmitDueDiligenceProject, dispatch]
  );

  const handleShareUrl = useCallback(() => {
    const url = new URL(window.location.href);
    const shareableUrl = url.toString();
    const shareableDealUrl = shareableUrl.includes("dealId") ? shareableUrl : `${shareableUrl}?dealId=${deal.id}`;
    navigator.clipboard.writeText(shareableDealUrl);
    return shareableDealUrl;
  }, [deal.id]);

  const handleShareDeal = useCallback(
    (email: string, message?: string) => {
      const shareableDealUrl = handleShareUrl();
      const entities: RequestEntities = [
        ...baseEntities,
        { entity: "email_address", value: email },
        { entity: "deal_url", value: shareableDealUrl },
      ];
      if (message) {
        entities.push({ entity: "message", value: message });
      }

      onSubmitWorkflow("share_deal", entities, `I'll share the ${deal.name} deal with ${email}.`);
    },
    [handleShareUrl, onSubmitWorkflow, baseEntities, deal.name]
  );

  const handleUpdateLogoOrBanner = useCallback(
    async (action: "logo" | "banner" | "analyze") => {
      const buttonKey = `update-${action}`;

      try {
        // Set loading state
        setLoadingStates((prev) => ({ ...prev, [buttonKey]: true }));

        // Call the appropriate API function
        switch (action) {
          case "logo":
            await generateTickerLogo({ tickerIds: [deal.id] });
            break;
          case "banner":
            await generateTickerBanner({ tickerIds: [deal.id] });
            break;
          case "analyze":
            await generateDueDiligenceProject({ tickerIds: [deal.id] });
            break;
          default:
            throw new Error(`Unknown action: ${action}`);
        }
        const message =
          action === "analyze"
            ? `I'll get onto analyzing ${deal.name} in the background now. You can monitor this in the workflow logs.`
            : `I've requested an update to the ${action} for ${deal.name}. The workflow has been scheduled and will process shortly. This may take a minute.`;

        handleToastMessage(message);
      } catch (error) {
        handleToastMessage(`I had an issue sending that request. Please check the admin and console logs.`);
        console.error(`Error updating ${action} for ${deal.name}:`, error);
      } finally {
        // Clear loading state
        setLoadingStates((prev) => ({ ...prev, [buttonKey]: false }));
      }
    },
    [deal.id, deal.name, handleToastMessage]
  );

  const handlePurchaseScorecard = useCallback((event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setIsPurchaseModalOpen(true);
  }, []);

  // Fetch ticker data using getPaginatedTickers and filter by deal ID
  const fetchTickerData = useCallback(async (dealId: string): Promise<Ticker | undefined> => {
    if (!dealId) {
      console.warn("Deal ID is not available, cannot fetch ticker data.");
      return undefined;
    }

    try {
      const response = await getPaginatedTickers({
        filterById: dealId,
      });
      return response.data[0];
    } catch (error) {
      console.error("Error fetching ticker data:", error);
      return undefined;
    }
  }, []);

  const handleCloseUpdateTicker = useCallback(() => {
    setFetchedTicker(undefined);
    onCloseUpdateTicker();
  }, [onCloseUpdateTicker]);

  const handleSubmitUpdateTicker = useCallback(
    (values: UpdateTickerForm, id?: string) => {
      if (!id) {
        return;
      }

      const convertToNumber = (value: string | number | null): number | null => {
        if (value === null || value === "") return null;
        const num = typeof value === "string" ? Number(value) : value;
        return isNaN(num) ? null : num;
      };

      const payload: UpdateTickerRequest = {
        name: values.name,
        exchange: values.exchange,
        dataProviderId: values.dataProviderId === "null" ? null : values.dataProviderId || null,
        status: values.status,
        notes: values.notes || null,
        companyLogoUrl: values.companyLogoUrl || null,
        companyBannerUrl: values.companyBannerUrl || null,
        companyUrl: values.companyUrl || null,
        companyTaxId: values.companyTaxId || null,
        companyLegalName: values.companyLegalName || null,
        pricingTier: values.pricingTier === "null" ? null : values.pricingTier || null,
        ...(values.type !== "null" && { type: values.type }),
        investmentScoreAnalytical: convertToNumber(values.investmentScoreAnalytical),
        investmentScoreSentiment: convertToNumber(values.investmentScoreSentiment),
        overallInvestmentScore: convertToNumber(values.overallInvestmentScore),
        marketCap: convertToNumber(values.marketCap),
        fairValue: convertToNumber(values.fairValue),
        sellRating: values.sellRating,
        revenue: convertToNumber(values.revenue),
        revenueGrowthRate: convertToNumber(values.revenueGrowthRate),
        country: values.country || null,
        stateOrProvince: values.stateOrProvince || null,
        city: values.city || null,
        address: values.address || null,
        yearFounded: convertToNumber(values.yearFounded),
        industries: values.industries,
        companyDescription: values.companyDescription || null,
        companyType: values.companyType || null,
        companyLegalStructure: values.companyLegalStructure || null,
        numberOfEmployees: convertToNumber(values.numberOfEmployees),
        ipoStatus: values.ipoStatus || null,
        companyTradingStatus: values.companyTradingStatus || null,
        companyStockExchangeStatus: values.companyStockExchangeStatus || null,
        companyCreditScore: values.companyCreditScore || null,
        raisingCapital: values.raisingCapital || null,
        raisingCapitalAmount: values.raisingCapitalAmount || null,
        companyLinkedinUrl: values.companyLinkedinUrl || null,
        companyCrunchbaseUrl: values.companyCrunchbaseUrl || null,
        dataProviderConfidenceScore: values.dataProviderConfidenceScore || null,
        inferredCompanyStage: values.inferredCompanyStage || null,
        inferredIndustries: values.inferredIndustries,
        inferredGrowthRate: convertToNumber(values.inferredGrowthRate),
        inferredRevenue: convertToNumber(values.inferredRevenue),
        additionalContext: "Updated from the deal finder edit pop up",
      };

      setIsLoadingUpdateTicker(true);
      updateTicker({ id, ticker: payload })
        .then(() => {
          handleToastMessage(`The company catalog for ${deal.name} has been updated.`);
          handleCloseUpdateTicker();
        })
        .catch((error) => {
          toast({
            title: `Error updating company: ${error.message}`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsLoadingUpdateTicker(false);
        });
    },
    [handleToastMessage, deal.name, handleCloseUpdateTicker, toast]
  );

  const handleOpenUpdateTicker = useCallback(
    async (event: { stopPropagation: () => void }) => {
      event.stopPropagation();

      // Show loading state
      setIsLoadingUpdateTicker(true);

      try {
        // Fetch the actual ticker data
        const tickerData = await fetchTickerData(deal.id);

        if (tickerData) {
          setFetchedTicker(tickerData);
          onOpenUpdateTicker();
        } else {
          toast({
            title: "Ticker not found",
            description: "Could not find ticker data for this company",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          title: "Error loading ticker data",
          description: "Failed to fetch ticker information",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoadingUpdateTicker(false);
      }
    },
    [deal, fetchTickerData, onOpenUpdateTicker, toast]
  );

  const handleClaimCompany = useCallback(
    (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      if (deal.symbol && deal.exchange) {
        dispatch(dealFinderActions.setSelectedDeal(deal));
        navigate(`/claim/${deal.symbol}/${deal.exchange}`);
      }
    },
    [deal, dispatch, navigate]
  );

  const handleToggleTickerStatus = useCallback(
    async (event: { stopPropagation: () => void }) => {
      event.stopPropagation();

      setIsLoadingUpdateTicker(true);

      try {
        await updateTicker({
          id: deal.id,
          ticker: { status: "inactive", additionalContext: "Updated from the deal finder drop down menu" },
        });
        handleToastMessage(`The company catalog for ${deal.name} has been successfully deactivated`);
      } catch (error) {
        toast({
          title: `Error deactivating company`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoadingUpdateTicker(false);
      }
    },
    [deal.id, deal.name, handleToastMessage, toast]
  );

  const renderMenuButton = useCallback(
    (config: MenuButtonConfig, index: number) => {
      const buttonKey = config.action ?? `share-${index}`; // Use 'share' for the share button which lacks an action
      const isLoading = loadingStates[buttonKey] ?? false;
      const isLikeButton = index === 0; // Like button is always the first one
      const showCount = isLikeButton && config.likedCount && config.likedCount > 0;

      let finalTooltip = config.tooltip;
      if (showCount) {
        // Ensure likedCount is not null before using it in calculations
        if (config.likedCount) {
          if (config.isActive) {
            // deal.hasUserLiked
            finalTooltip =
              config.likedCount === 1
                ? "You like this deal"
                : `You and ${config.likedCount - 1} other${config.likedCount > 2 ? "s" : ""} like this deal`;
          } else {
            finalTooltip = `${config.likedCount} ${config.likedCount === 1 ? "person" : "people"} like${
              config.likedCount === 1 ? "s" : ""
            } this deal`;
          }
        }
      }

      const buttonElement = (
        <SmallActionButton
          classNames={`ch-deal-menu-buttons ch-deal-menu-button-${buttonKey}`}
          iconSize={iconSize}
          iconColor={isLoading || config.isActive ? activeIconColor : undefined}
          tooltip={config.tooltip}
          iconTypeName={config.icon as IconType}
          isDisabled={isLoading || isAnonymousUser}
          zIndex={2}
          onClick={() => {
            if (config.action) {
              setLoadingStates((prev) => ({ ...prev, [buttonKey]: true }));
              onSubmitWorkflow(config.action, [...baseEntities], config.message);
              setTimeout(() => {
                setLoadingStates((prev) => ({ ...prev, [buttonKey]: false }));
              }, 3000);
            }
          }}
        />
      );

      if (showCount) {
        return (
          <HStack position={"relative"} key={`action-container-${buttonKey}`} spacing={1.5} alignItems="center">
            {buttonElement}
            <Tooltip label={finalTooltip} placement="right">
              <Circle
                bgColor={activeBgCountColor}
                size="1rem"
                position="absolute"
                top="0"
                left={displayOption === "list" ? "-20px" : "20px"}>
                <Text fontSize="xs" fontWeight="medium" color={activeCountColor} userSelect="none">
                  {config.likedCount}
                </Text>
              </Circle>
            </Tooltip>
          </HStack>
        );
      } else {
        return React.cloneElement(buttonElement, { key: `action-button-${buttonKey}` });
      }
    },
    [
      loadingStates,
      iconSize,
      activeIconColor,
      isAnonymousUser,
      onSubmitWorkflow,
      baseEntities,
      activeBgCountColor,
      displayOption,
      activeCountColor,
    ]
  );

  const actionButtonsGroup = useMemo(
    () => (
      <>
        {menuButtons.slice(0, 1).map(renderMenuButton)}
        <Tooltip label="Coming soon" placement="left">
          <Box lineHeight={"0"}>
            <SmallActionButton
              isDisabled
              classNames="ch-deal-menu-button-share"
              key="action-button-share"
              iconSize={iconSize}
              iconTypeName={BiShareAlt as IconType}
              onClick={() => setIsShareModalOpen(true)}
            />
          </Box>
        </Tooltip>
        {menuButtons.slice(2).map(renderMenuButton)}
        <Tooltip label={`${showCompetitorsCopy} ${deal.name}`} placement="left">
          <Box lineHeight={"0"}>
            <SmallActionButton
              classNames="ch-deal-show-competitors"
              key="ch-deal-show-competitors"
              iconSize={iconSize}
              iconTypeName={PiNetwork as IconType}
              onClick={() => {
                isOpen && closeModal();
                onSubmitDealFinderQuery(`${showCompetitorsCopy} ${deal.name}, ${deal.symbol} on ${deal.exchange}`);
              }}
            />
          </Box>
        </Tooltip>
      </>
    ),
    [
      menuButtons,
      renderMenuButton,
      iconSize,
      showCompetitorsCopy,
      deal.name,
      deal.symbol,
      deal.exchange,
      isOpen,
      closeModal,
      onSubmitDealFinderQuery,
    ]
  );

  const menuOptions = useMemo(
    () => (
      <>
        <MenuList minWidth={"unset"} zIndex={10} boxShadow={"md"}>
          <OptionsMenuItem
            menuLabel="More Like This"
            className="ch-deal-more-like-this"
            onClick={(event) => {
              event.stopPropagation();
              isOpen && closeModal();
              onSubmitDealFinderQuery(`${moreLikeThisCopy} ${deal.name}, ${deal.symbol} on ${deal.exchange}`);
            }}
          />
          {!deal.hasUserPurchased && (
            <OptionsMenuItem
              isDisabled={isAnonymousUser || !isDealStatusAllowed}
              menuLabel="Purchase Scorecard"
              className="ch-deal-buy-scorecard"
              onClick={handlePurchaseScorecard}
            />
          )}
          <MenuDivider />
          {deal.companyUrl && (
            <OptionsMenuItem
              isDisabled={deal.companyUrl === ""}
              menuLabel="Open Website"
              className="ch-deal-open-website"
              onClick={(event) => {
                event.stopPropagation();
                isOpen && closeModal();
                deal.companyUrl && window.open(deal.companyUrl, "_blank");
              }}
            />
          )}
          {!isAnonymousUser && (
            <>
              <OptionsMenuItem
                isDisabled={isRestricted || !hasEnoughCredit || !isDealStatusAllowed}
                menuLabel="Analyze Investment"
                className="ch-deal-analyze-investment"
                onClick={(event) => submitDueDiligence(event)}
              />
            </>
          )}
          <OptionsMenuItem
            menuLabel="Report issue with company"
            className="ch-deal-report-issue"
            onClick={(event) => {
              event.stopPropagation();
              isOpen && closeModal();
              window.open("https://support.charli.ai/hc/en-us/requests/new", "_blank");
            }}
          />
          {hasIRDashboardAccess && (
            // TODO: update to (hasIRDashboardAccess || isAnonymousUser) when this is ready for production
            <>
              <MenuDivider />
              <OptionsMenuItem menuLabel="Claim Company" className="ch-deal-claim-company" onClick={handleClaimCompany} />
              <OptionsMenuItem
                menuLabel="Investor Readiness"
                className="ch-deal-investor-readiness"
                onClick={(event) => {
                  event.stopPropagation();
                  onSubmitWorkflow(
                    "create_investor_readiness_project",
                    [
                      { entity: "company_ticker", value: deal.symbol },
                      { entity: "company_stock_exchange", value: deal.exchange },
                    ],
                    `I'll create an investor readiness project for ${deal.name} now.`
                  );
                }}
              />
            </>
          )}
          {hasCompanyCatalogAdmin && (
            <>
              <MenuDivider />
              <OptionsMenuItem
                menuLabel="Analyze in background"
                className="ch-deal-analyze-in-background"
                onClick={(event) => {
                  event.stopPropagation();
                  handleUpdateLogoOrBanner("analyze");
                }}
              />
              <OptionsMenuItem
                isDisabled={
                  !deal.companyUrl || deal.companyUrl === "" || !deal.symbol || deal.symbol === "" || !deal.exchange || deal.exchange === ""
                }
                menuLabel="Update Banner"
                className="ch-deal-update-banner"
                onClick={(event) => {
                  event.stopPropagation();
                  handleUpdateLogoOrBanner("banner");
                }}
              />
              <OptionsMenuItem
                isDisabled={!deal.name || deal.name === "" || !deal.symbol || deal.symbol === "" || !deal.exchange || deal.exchange === ""}
                menuLabel="Update Logo"
                className="ch-deal-update-logo"
                onClick={(event) => {
                  event.stopPropagation();
                  handleUpdateLogoOrBanner("logo");
                }}
              />
              <OptionsMenuItem
                menuLabel="Make Inactive"
                className="ch-deal-make-inactive"
                onClick={(event) => handleToggleTickerStatus(event)}
              />
              <OptionsMenuItem
                menuLabel="Edit Company Catalog"
                className="ch-deal-open-company-catalog"
                onClick={(event) => handleOpenUpdateTicker(event)}
              />
            </>
          )}
        </MenuList>
      </>
    ),
    [
      deal.hasUserPurchased,
      deal.companyUrl,
      deal.symbol,
      deal.exchange,
      deal.name,
      isAnonymousUser,
      isDealStatusAllowed,
      handlePurchaseScorecard,
      isRestricted,
      hasEnoughCredit,
      hasIRDashboardAccess,
      handleClaimCompany,
      hasCompanyCatalogAdmin,
      isOpen,
      closeModal,
      onSubmitDealFinderQuery,
      moreLikeThisCopy,
      submitDueDiligence,
      onSubmitWorkflow,
      handleUpdateLogoOrBanner,
      handleToggleTickerStatus,
      handleOpenUpdateTicker,
    ]
  );

  return (
    <>
      <ShareEmailModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} deal={deal} onShare={handleShareDeal} />
      {isPurchaseModalOpen && (
        <PurchasingCartModal
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          deal={deal}
          selectedProductKey={"scorecard"}
        />
      )}
      <UpdateTickerPanel
        isLoading={isLoadingUpdateTicker}
        ticker={fetchedTicker}
        isOpen={isUpdateTickerOpen}
        onClose={handleCloseUpdateTicker}
        onSubmit={handleSubmitUpdateTicker}
      />
      <Stack
        direction="row"
        width="100%"
        justify="space-between"
        px={"1rem"}
        py={".5rem"}
        borderTop={showBorder ? "1px solid" : undefined}
        borderColor={borderColor}
        ref={containerRef}>
        <>
          {isWideContainer ? (
            <Stack direction="row" spacing={"2rem"}>
              {actionButtonsGroup}
            </Stack>
          ) : (
            actionButtonsGroup
          )}
          <Menu isLazy placement="bottom-end">
            <Tooltip label="More Actions" placement="left">
              <MenuButton
                height="unset"
                width="1rem"
                minWidth={"unset"}
                paddingInline={0}
                border="none"
                _hover={{
                  color: "primary.default",
                }}
                _active={{}}
                color={defaultIconColor}
                bgColor={"transparent"}
                className="ch-content-options"
                as={Button}
                aria-label="Options"
                rightIcon={<BiDotsVertical color={defaultIconColor} fontSize={iconSize} />}
                onClick={(event) => event.stopPropagation()}></MenuButton>
            </Tooltip>
            {isWideContainer ? menuOptions : <Portal>{menuOptions}</Portal>}
          </Menu>
        </>
      </Stack>
    </>
  );
};
